import { useCallback, useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { MdLock } from 'react-icons/md';
import Loading from '@/components/Loading';
import useProvidedJobPostListShow from '../api/useProvidedJobPostListShow';
import useProvidedJobPosts from '@/features/agent/provided_job_posts/hooks/useProvidedJobPosts';
import useFetchIam from '@/features/agent/api/useFetchIam';
import useRemoveItemsFromList from '../api/useRemoveItemsFromList';
import ProvidedJobPostCard from '@/features/agent/provided_job_posts/components/ProvidedJobPostCard';
import ListActionMenu from '../components/ListActionMenu';
import { showSuccessToast } from '@/features/agent/job_change_processes/lib/toast';
import { useSetBreadcrumbTitle } from '@/hooks/useBreadcrumb';
import type { SearchQuery } from '@/features/agent/provided_job_posts/hooks/useProvidedJobPosts';

interface Props {
  listId: string;
}

export default function ProvidedJobPostListDetailTemplate({ listId }: Props) {
  const navigate = useNavigate();
  const { data: list, isLoading: isListLoading } = useProvidedJobPostListShow(listId);
  const { data: iam } = useFetchIam();

  // パンくずリストにリスト名を設定
  useSetBreadcrumbTitle(list?.name);
  const { mutateAsync: removeItems } = useRemoveItemsFromList();
  const currentUserId = iam?.current_user?.id;

  const searchQuery: SearchQuery = useMemo(
    () => ({
      company_ids: [],
      custom_tag_ids: [],
      list_ids: list ? [list.id] : [],
      sort: 'updated_at_desc',
    }),
    [list]
  );

  const {
    data: jobPostsData,
    isLoading: isJobPostsLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useProvidedJobPosts(searchQuery, { enabled: !!list });

  const jobPosts = useMemo(
    () => jobPostsData?.pages.flatMap((page) => page.records) ?? [],
    [jobPostsData]
  );

  const handleRemoveFromList = useCallback(
    async (jobPostId: number) => {
      try {
        await removeItems({
          listId,
          providedJobPostIds: [jobPostId],
        });
        showSuccessToast('求人をリストから削除しました');
      } catch {
        alert('リストからの削除に失敗しました');
      }
    },
    [listId, removeItems]
  );

  const handleListDeleted = useCallback(() => {
    navigate({ to: '/agent/provided_job_post_lists' as never });
  }, [navigate]);

  if (isListLoading) {
    return (
      <div className="flex justify-center py-16">
        <Loading size="medium" />
      </div>
    );
  }

  if (!list) {
    return (
      <div className="flex flex-col items-center gap-4 p-8">
        <p className="text-lg text-gray-500">リストが見つかりません</p>
      </div>
    );
  }

  const isOwner = currentUserId === list.created_by_id;

  return (
    <div className="flex flex-col gap-6 p-8">
      {/* ヘッダー */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-bold">{list.name}</h1>
          {!list.is_public && <MdLock className="size-5" />}
        </div>
        <ListActionMenu
          listId={list.uuid}
          listName={list.name}
          isPublic={list.is_public}
          isOwner={isOwner}
          onDeleted={handleListDeleted}
        />
      </div>

      {/* 求人一覧 */}
      {isJobPostsLoading ? (
        <div className="flex justify-center py-16">
          <Loading size="medium" />
        </div>
      ) : jobPosts.length === 0 ? (
        <div className="flex flex-col items-center gap-4 py-16 text-gray-500">
          <p className="text-lg">求人がありません</p>
          <p className="text-sm">求人管理画面からリストに求人を追加してください。</p>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {jobPosts.map((jobPost) => (
            <ProvidedJobPostCard
              key={jobPost.id}
              {...jobPost}
              hideDuplicate
              hideDelete
              extraMenuItems={
                isOwner ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveFromList(jobPost.id);
                    }}
                    className="flex h-xxl items-center gap-xs px-md py-xs text-body-sm text-danger hover:bg-gray"
                  >
                    リストから削除
                  </button>
                ) : undefined
              }
            />
          ))}

          {hasNextPage && (
            <div className="flex justify-center py-4">
              <button
                type="button"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                className="rounded border border-solid border-border bg-white px-6 py-2 text-sm font-bold hover:bg-gray-50"
              >
                {isFetchingNextPage ? '読み込み中...' : 'もっと見る'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
